import React, { useEffect, useRef, useMemo } from "react";
import { emojify } from "node-emoji";
import "./MadPropsCarousel.css";

// Skin tone modifier map
const skinToneMap = {
  ":skin-tone-2:": "🏻",
  ":skin-tone-3:": "🏼",
  ":skin-tone-4:": "🏽",
  ":skin-tone-5:": "🏾",
  ":skin-tone-6:": "🏿",
};

const canHaveSkinTone = (emoji) => {
  const skinToneEmojiRegex =
    /[👋👌👍👎👊✊🤛🤜👏👐🙌🤲🙏✍️💅🤳💪🦵🦶👂👃🧠🦷🦴👀👁️👅👄]/;
  return skinToneEmojiRegex.test(emoji);
};

const handleSkinToneModifiers = (text) => {
  if (!text) return text;
  const skinToneRegex = /:([\w-]+):(:skin-tone-[2-6]:)/g;
  return text.replace(skinToneRegex, (match, emojiName, skinTone) => {
    const baseEmoji = emojify(`:${emojiName}:`);
    if (baseEmoji === `:${emojiName}:` || !canHaveSkinTone(baseEmoji)) {
      return match;
    }
    const skinToneModifier = skinToneMap[skinTone] || "";
    return baseEmoji + skinToneModifier;
  });
};

const processMessageContent = (htmlContent) => {
  if (!htmlContent) return htmlContent;
  let processedContent = handleSkinToneModifiers(htmlContent);
  return emojify(processedContent);
};

const renderEmoji = (emojiCode) => {
  if (emojiCode.startsWith("http")) {
    return <img src={emojiCode} alt="emoji" className="carousel-emoji-image" />;
  }
  if (emojiCode.includes(":skin-tone-")) {
    const parts = emojiCode.split(":");
    if (parts.length >= 4) {
      const baseEmojiName = parts[1];
      const skinTone = `:skin-tone-${parts[3]}:`;
      const baseEmoji = emojify(`:${baseEmojiName}:`);
      if (baseEmoji !== `:${baseEmojiName}:` && canHaveSkinTone(baseEmoji)) {
        const skinToneModifier = skinToneMap[skinTone] || "";
        return (
          <span>
            {baseEmoji}
            {skinToneModifier}
          </span>
        );
      }
    }
  }
  return <span>{emojify(emojiCode)}</span>;
};

const CARD_WIDTH = 340;
const CARD_GAP = 20;
const CARD_STEP = CARD_WIDTH + CARD_GAP;
const DEFAULT_SPEED = 0.15; // pixels per frame — very slow for readability

const CardItem = ({ msg }) => {
  const processedContent = processMessageContent(msg.content);
  return (
    <div className="carousel-card">
      <div className="carousel-card-user">
        <img
          src={
            msg.user.image ||
            "https://secure.gravatar.com/avatar/697ff15fe1db18780ed38cc4d86c3fe3.jpg?s=512&d=https%3A%2F%2Fa.slack-edge.com%2Fdf10d%2Fimg%2Favatars%2Fava_0003-512.png"
          }
          alt={msg.user.real_name}
          className="carousel-avatar"
        />
        <span className="carousel-user-name">{msg.user.real_name}</span>
      </div>
      <div
        className="carousel-card-content"
        dangerouslySetInnerHTML={{ __html: processedContent }}
      />
      <div className="carousel-card-reactions">
        {msg.reactions &&
          msg.reactions.map((reaction, ri) => (
            <span key={ri} className="carousel-reaction">
              {reaction.variants.map((variant, vi) => (
                <span key={vi} className="carousel-emoji-variant">
                  {renderEmoji(variant.emoji)}
                </span>
              ))}
              <span className="carousel-reaction-count">
                {reaction.total_count}
              </span>
            </span>
          ))}
      </div>
    </div>
  );
};

const MadPropsCarousel = ({ messages = [], reverse = false, speed = DEFAULT_SPEED }) => {
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const animFrameRef = useRef(null);

  // Duplicate messages so we can loop seamlessly:
  // render [A, B, C, A, B, C] — when we scroll past the first set, snap back to 0
  const doubledMessages = useMemo(() => {
    if (messages.length === 0) return [];
    return [...messages, ...messages];
  }, [messages]);

  const singleSetWidth = messages.length * CARD_STEP;

  useEffect(() => {
    if (messages.length === 0 || singleSetWidth === 0) return;

    // When reversing, start with the second copy visible so we can decrease
    // offset toward 0 and snap back to singleSetWidth seamlessly.
    offsetRef.current = reverse ? singleSetWidth : 0;
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
    }

    const animate = () => {
      if (reverse) {
        offsetRef.current -= speed;
        if (offsetRef.current <= 0) {
          offsetRef.current += singleSetWidth;
        }
      } else {
        offsetRef.current += speed;
        if (offsetRef.current >= singleSetWidth) {
          offsetRef.current -= singleSetWidth;
        }
      }

      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [messages.length, singleSetWidth, reverse, speed]);

  if (messages.length === 0) {
    return (
      <div className="carousel-state-msg">No mad props available</div>
    );
  }

  return (
    <div className="mad-props-carousel">
      <div className="carousel-track" ref={trackRef}>
        {doubledMessages.map((msg, i) => (
          <CardItem msg={msg} key={i} />
        ))}
      </div>
    </div>
  );
};

export default MadPropsCarousel;
